"use client";

import {
  Box,
  Button,
  Flex,
  Heading,
  ProgressCircle,
  Stack,
  Switch,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { LuArrowLeft, LuCircleCheck } from "react-icons/lu";

import CargoItemsTable from "@features/services/shipping/CargoItemsTable";
import DocumentUploadDropzone from "@features/services/shipping/DocumentUploadDropzone";
import { extractCargoItems } from "@features/services/shipping/extractCargoItems";
import {
  ShippingLabel,
  SenderForm,
  type SenderInfo,
} from "@features/services/shipping/ShippingLabel";
import type { CargoItem } from "@features/services/shipping/types";
import { makeDetailedAddress } from "@features/services/shipping/deliveryAddress";
import {
  classifyGoodsLines,
  estimateFreightCost,
} from "@features/services/shipping/rates";
import {
  footprintFormatters,
  totalCargoFootprint,
} from "helpers/pricingUnit";
import { filesToBase64Payloads } from "helpers/fileEncoding";
import { generateUniqueTrackingRefAction } from "server/actions/trackingRef";
import {
  createShipmentWithDetailsAction,
  lookupShipmentAction,
} from "server/actions/shipment";
import { useStores } from "stores/rootStore";
import { useOrgId } from "hooks/useOrgId";

const ACCEPTED_TYPES =
  "application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv";
const MAX_FILE_SIZE_BYTES = 3 * 1024 * 1024;

type Phase = "form" | "submitting" | "done";

const InlineSpinner = ({ label }: { label: string }) => (
  <Flex align="center" justify="center" gap={2} py={8}>
    <ProgressCircle.Root value={null} size="xs">
      <ProgressCircle.Circle>
        <ProgressCircle.Track />
        <ProgressCircle.Range />
      </ProgressCircle.Circle>
    </ProgressCircle.Root>
    <Text fontSize="sm" color="text.secondary">
      {label}
    </Text>
  </Flex>
);

interface CargoDetailsViewProps {
  bookingOpen: boolean;
  onBack: () => void;
  onTrackingRefGenerated?: (ref: string) => void;
  initialTrackingRef?: string | null;
}

const noop = () => {};

const CargoDetailsView = ({
  bookingOpen,
  onBack,
  onTrackingRefGenerated,
  initialTrackingRef,
}: CargoDetailsViewProps) => {
  const orgId = useOrgId();
  const { userStore } = useStores();

  const [phase, setPhase] = useState<Phase>("form");
  const [items, setItems] = useState<CargoItem[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackingRef, setTrackingRef] = useState<string | null>(
    initialTrackingRef ?? null,
  );
  const [warehouseAddress, setWarehouseAddress] = useState<string | null>(null);
  const [label, setLabel] = useState<{
    senderName: string | null;
    goodsDescription: string | null;
  } | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(
    initialTrackingRef != null,
  );
  const [showUpload, setShowUpload] = useState(false);

  const replaceInputRef = useRef<HTMLInputElement>(null);

  // Bookings are priced per CBM from the goods rate card; the final figure
  // comes from the warehouse's measurement after the goods arrive.
  const fmt = footprintFormatters("CBM");
  const totalUnits = totalCargoFootprint(items, "CBM");
  const estimate = classifyGoodsLines(
    items.map((item) => item.description),
    totalUnits,
  );
  const estimatedCost = estimateFreightCost(estimate, totalUnits).toFixed(2);

  const runExtraction = useCallback(async (selected: File[]) => {
    setError(null);
    const oversized = selected.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      setError("One or more files exceed the 3 MB size limit.");
      return;
    }
    setExtracting(true);
    try {
      const newItems = await extractCargoItems(selected);
      setItems(newItems);
      setFiles(selected);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Extraction failed");
    } finally {
      setExtracting(false);
    }
  }, []);

  const onFilesChange = useCallback(
    (selected: File[]) => {
      if (selected.length > 0) {
        runExtraction(selected);
      }
    },
    [runExtraction],
  );

  const onReplaceCargo = useCallback(() => {
    replaceInputRef.current?.click();
  }, []);

  const onReplaceFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files;
      if (selected != null && selected.length > 0) {
        runExtraction(Array.from(selected));
      }
      e.target.value = "";
    },
    [runExtraction],
  );

  const onToggleUpload = useCallback((e: { checked: boolean }) => {
    setShowUpload(e.checked);
    // Toggling off means "no packing list" — drop anything already extracted.
    if (!e.checked) {
      setItems([]);
      setFiles([]);
    }
  }, []);

  const onRemoveCargo = useCallback(() => {
    setItems([]);
    setFiles([]);
    setShowUpload(false);
  }, []);

  const onSubmitDetails = useCallback(
    async (sender: SenderInfo) => {
      if (trackingRef == null) {
        return;
      }
      setError(null);
      setPhase("submitting");
      try {
        const filePayloads = await filesToBase64Payloads(files);
        const result = await createShipmentWithDetailsAction(
          orgId,
          trackingRef,
          items,
          filePayloads,
          {
            senderName: sender.name,
            senderPhone: sender.phone,
            senderYob: Number(sender.yob),
            deliveryAddress: makeDetailedAddress({
              area: sender.area,
              landmark: sender.landmark,
              ghanaPostCode: sender.ghanaPostCode,
              lat: sender.lat,
              lng: sender.lng,
            }),
            goodsDescription: sender.goodsDescription,
          },
        );
        if ("error" in result) {
          setError(result.error);
          setPhase("form");
          return;
        }
        setWarehouseAddress(result.warehouseAddress);
        setLabel({
          senderName: sender.name,
          goodsDescription: sender.goodsDescription,
        });
        setPhase("done");
      } catch {
        setError(
          "Something went wrong creating your shipment. Please try again.",
        );
        setPhase("form");
      }
    },
    [orgId, trackingRef, items, files],
  );

  // Resume an existing shipment (opened from a ?tracking=… link): load it and
  // show its label + read-only cargo summary. Editing isn't supported here.
  useEffect(() => {
    if (initialTrackingRef == null) {
      return;
    }
    let cancelled = false;
    lookupShipmentAction(orgId, initialTrackingRef)
      .then((result) => {
        if (cancelled || result == null) {
          return;
        }
        setItems(result.packingList ?? []);
        setWarehouseAddress(result.warehouseAddress);
        setLabel({
          senderName: result.senderName,
          goodsDescription: result.goodsDescription,
        });
        setPhase("done");
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingExisting(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [initialTrackingRef, orgId]);

  useEffect(() => {
    if (initialTrackingRef != null) {
      return;
    }
    let cancelled = false;
    generateUniqueTrackingRefAction(orgId, "ELK").then((ref) => {
      if (!cancelled) {
        setTrackingRef(ref);
        onTrackingRefGenerated?.(ref);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [initialTrackingRef, onTrackingRefGenerated, orgId]);

  if (loadingExisting) {
    return (
      <Flex direction="column" align="center" gap={2} p="40px 20px">
        <Flex w="100%" maxW="640px" direction="column" gap={4}>
          <InlineSpinner label="Loading..." />
        </Flex>
      </Flex>
    );
  }

  const isDone = phase === "done";

  return (
    <Flex direction="column" align="center" gap={2} p="40px 20px">
      <Flex w="100%" maxW="640px" direction="column" gap={5}>
        <Button
          variant="ghost"
          size="sm"
          alignSelf="flex-start"
          px={0}
          _hover={{ bg: "transparent" }}
          onClick={onBack}
        >
          <LuArrowLeft />
          Back
        </Button>

        <Stack gap={1}>
          <Heading
            fontSize="lg"
            fontWeight={600}
            letterSpacing=".06em"
            color="text.primary"
          >
            {isDone ? "Your shipping label" : "Ship with us"}
          </Heading>
          {!isDone && (
            <Text fontSize="sm" color="text.secondary">
              Get your shipping label in minutes. Your rate depends on what
              you ship, and the final bill uses the volume we measure at the
              warehouse.
            </Text>
          )}
        </Stack>

        {error != null && (
          <Text fontSize="sm" color="red.400">
            {error}
          </Text>
        )}

        {isDone ? (
          <Stack gap={5}>
            <Flex
              align="center"
              gap={2}
              p={3}
              borderRadius="md"
              bg="bg.subtle"
              borderWidth="1px"
              borderColor="border.muted"
            >
              <Box as={LuCircleCheck} color="green.500" boxSize={5} />
              <Text fontSize="sm" color="text.primary">
                Shipment created. Print or save your label and attach it to your
                cargo.
              </Text>
            </Flex>

            {items.length > 0 && (
              <Stack gap={2}>
                <Text fontSize="sm" fontWeight="semibold" color="text.primary">
                  Cargo summary
                </Text>
                <CargoItemsTable
                  items={items}
                  pricingUnit="CBM"
                  onRemove={noop}
                  onAddMore={noop}
                  locked
                />
              </Stack>
            )}

            {trackingRef != null && (
              <ShippingLabel
                trackingRef={trackingRef}
                warehouseAddress={warehouseAddress}
                customerName={label?.senderName ?? null}
                goodsDescription={label?.goodsDescription}
              />
            )}
          </Stack>
        ) : !bookingOpen ? (
          <Text fontSize="sm" color="text.secondary">
            Booking is temporarily unavailable. Your saved link will work again
            once bookings reopen.
          </Text>
        ) : (
          <Stack gap={3}>
            <Heading size="sm" color="text.primary">
              Your details
            </Heading>
            <SenderForm
              onSubmit={onSubmitDetails}
              submitting={phase === "submitting"}
              defaultName={userStore.user?.name}
              cargoCbm={items.length > 0 ? totalUnits : null}
              extraContent={
                <Stack
                  gap={3}
                  pt={3}
                  borderTopWidth="1px"
                  borderColor="border.light"
                >
                  <Flex justify="space-between" align="center" gap={4}>
                    <Stack gap={0}>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color="text.primary"
                      >
                        Upload packing list
                      </Text>
                      <Text fontSize="xs" color="text.secondary">
                        Optional — we&apos;ll read your items automatically.
                      </Text>
                    </Stack>
                    <Switch.Root
                      checked={showUpload}
                      onCheckedChange={onToggleUpload}
                    >
                      <Switch.HiddenInput />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Root>
                  </Flex>

                  {showUpload &&
                    (extracting ? (
                      <InlineSpinner label="Reading your documents..." />
                    ) : items.length > 0 ? (
                      <Stack gap={2}>
                        <Flex
                          justify="space-between"
                          align="center"
                          flexWrap="wrap"
                          gap={2}
                        >
                          <Text fontSize="sm" color="text.secondary">
                            Extracted {items.length} item
                            {items.length === 1 ? "" : "s"} ·{" "}
                            {fmt.formatTotal(totalUnits)} ·{" "}
                            {estimate.perPieceQuote
                              ? "price on request"
                              : `est. ${estimatedCost} USD`}
                          </Text>
                          <Flex gap={2}>
                            <Button
                              size="xs"
                              variant="surfaceOutline"
                              onClick={onReplaceCargo}
                            >
                              Replace
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={onRemoveCargo}
                            >
                              Remove
                            </Button>
                          </Flex>
                        </Flex>
                        <CargoItemsTable
                          items={items}
                          pricingUnit="CBM"
                          onRemove={noop}
                          onAddMore={noop}
                          locked
                        />
                      </Stack>
                    ) : (
                      <DocumentUploadDropzone onFilesChange={onFilesChange} />
                    ))}

                  <input
                    ref={replaceInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    multiple
                    hidden
                    onChange={onReplaceFileChange}
                  />
                </Stack>
              }
            />
          </Stack>
        )}
      </Flex>
    </Flex>
  );
};

export default CargoDetailsView;
